import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { MdClose, MdAnchor, MdDirectionsBoat, MdAttachMoney, MdRule, MdSupportAgent, MdWarehouse } from 'react-icons/md';
import { authenticatedFetch } from '../../../lib/api';
import { PageSkeleton, erpPageBtnSecondaryClass } from '@euroasiann/shared/ui';
import {
  pmOverlayClass,
  pmDrawerClass,
  pmSectionTitleClass,
  pmCardClass,
  pmStatusClass,
} from '../port-management.ui';

const CHARGE_LABELS: Record<string, string> = {
  port_dues: 'Port Dues',
  wharfage: 'Wharfage / Dock',
  dry_dock: 'Dry Dock',
  parking: 'Parking',
  pilotage: 'Pilotage',
  towage: 'Towage',
  mooring: 'Mooring',
  utilities: 'Utilities',
  storage: 'Storage',
  other: 'Other',
};

interface PortDetailPanelProps {
  portId: string;
  onClose: () => void;
}

export function PortDetailPanel({ portId, onClose }: PortDetailPanelProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['port-full-profile', portId],
    queryFn: async () => {
      const res = await authenticatedFetch(`/api/v1/ports/${portId}/full`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to load port profile');
      return json.data;
    },
    enabled: !!portId,
  });

  const overlay = (
    <div className={`${pmOverlayClass} flex justify-end`} onClick={onClose}>
      {isLoading || error || !data ? (
        <div className={pmDrawerClass} onClick={(e) => e.stopPropagation()}>
          <div className="flex items-start justify-between border-b border-[hsl(var(--border))] px-6 py-4">
            <h2 className="text-lg font-semibold text-[hsl(var(--foreground))]">Port details</h2>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]"
              aria-label="Close"
            >
              <MdClose className="h-5 w-5" />
            </button>
          </div>
          <div className="p-6">
            {isLoading ? (
              <PageSkeleton variant="panel" />
            ) : (
              <div>
                <p className="mb-4 text-sm font-medium text-rose-600 dark:text-rose-400">Failed to load port details</p>
                <button type="button" onClick={onClose} className={erpPageBtnSecondaryClass}>Close</button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <PortDetailContent data={data} onClose={onClose} />
      )}
    </div>
  );

  return createPortal(overlay, document.body);
}

function PortDetailContent({ data, onClose }: { data: any; onClose: () => void }) {
  const { port, terminals, berths, chargesByCategory, rules, agents, warehouses, summary } = data;

  return (
    <div className={pmDrawerClass} onClick={(e) => e.stopPropagation()}>
      <div className="flex items-start justify-between gap-3 border-b border-[hsl(var(--border))] px-6 py-4">
        <div className="min-w-0">
          <p className="mb-1 inline-flex items-center gap-1.5 text-xs font-medium text-[#0088cc]">
            <MdAnchor className="h-4 w-4" />
            {port.code} · {port.countryName || port.country}
          </p>
          <h2 className="text-xl font-semibold text-[hsl(var(--foreground))]">{port.name}</h2>
          <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
            {[port.city, port.stateName || port.state, port.countryName || port.country].filter(Boolean).join(', ')}
            {port.latitude != null && port.longitude != null && (
              <span className="ml-2 font-mono text-xs">
                ({port.latitude.toFixed(4)}, {port.longitude.toFixed(4)})
              </span>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 rounded-lg p-1.5 text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]"
          aria-label="Close"
        >
          <MdClose className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 space-y-6 overflow-y-auto p-6">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {[
            { label: 'Terminals', value: summary.terminalCount, icon: MdDirectionsBoat },
            { label: 'Berths', value: summary.berthCount, icon: MdAnchor },
            { label: 'Charges', value: summary.chargeCount, icon: MdAttachMoney },
            { label: 'Rules', value: summary.ruleCount, icon: MdRule },
            { label: 'Agents', value: summary.agentCount, icon: MdSupportAgent },
            { label: 'Warehouses', value: summary.warehouseCount, icon: MdWarehouse },
          ].map(({ label, value, icon: Icon }) => (
            <div key={label} className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3">
              <Icon className="mb-2 h-4 w-4 text-[#0088cc]" />
              <div className="text-lg font-semibold text-[hsl(var(--foreground))]">{value}</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">{label}</div>
            </div>
          ))}
        </div>

        {(port.maxDraft || port.maxLOA || port.maxDWT) && (
          <section>
            <h3 className={`${pmSectionTitleClass} mb-2`}>Operational limits</h3>
            <div className="flex flex-wrap gap-2">
              {port.maxDraft && (
                <span className="rounded-md bg-[hsl(var(--muted))] px-2.5 py-1 text-xs font-medium">Max draft: {port.maxDraft}m</span>
              )}
              {port.maxLOA && (
                <span className="rounded-md bg-[hsl(var(--muted))] px-2.5 py-1 text-xs font-medium">Max LOA: {port.maxLOA}m</span>
              )}
              {port.maxDWT && (
                <span className="rounded-md bg-[hsl(var(--muted))] px-2.5 py-1 text-xs font-medium">Max DWT: {port.maxDWT}t</span>
              )}
            </div>
          </section>
        )}

        <section>
          <h3 className={`${pmSectionTitleClass} mb-2`}>
            Terminals, berths & jetties ({terminals.length})
          </h3>
          {terminals.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">No terminals registered — add them under the Terminals tab.</p>
          ) : (
            <div className="space-y-2">
              {terminals.map((t: any) => (
                <div key={t._id} className={pmCardClass}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <span className="rounded-md bg-[#0088cc]/10 px-2 py-0.5 text-[11px] font-medium text-[#0088cc]">
                        {t.facilityType || 'Terminal'}
                      </span>
                      <h4 className="mt-1 text-sm font-semibold text-[hsl(var(--foreground))]">{t.name}</h4>
                      {t.code && <span className="font-mono text-xs text-[hsl(var(--muted-foreground))]">{t.code}</span>}
                    </div>
                    <span className={pmStatusClass(t.status === 'Active')}>{t.status}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-[hsl(var(--muted-foreground))]">
                    {t.cargoType && <span>Cargo: {t.cargoType}</span>}
                    {t.berthCount != null && <span>Berths: {t.berthCount}</span>}
                    {t.maxDraft != null && <span>Draft: {t.maxDraft}m</span>}
                    {t.maxLOA != null && <span>LOA: {t.maxLOA}m</span>}
                    {t.operatingHours && <span>Hours: {t.operatingHours}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {berths.length > 0 && (
          <section>
            <h3 className={`${pmSectionTitleClass} mb-2`}>Live berth status ({berths.length})</h3>
            <div className="grid grid-cols-2 gap-2">
              {berths.map((b: any) => (
                <div key={b._id} className={pmCardClass}>
                  <div className="text-sm font-medium">{b.berthId}</div>
                  <div className={`mt-1 text-xs font-medium ${b.status === 'Vacant' ? 'text-emerald-600 dark:text-emerald-400' : b.status === 'Occupied' ? 'text-rose-600 dark:text-rose-400' : 'text-amber-600 dark:text-amber-400'}`}>
                    {b.status}
                  </div>
                  <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">L:{b.length}m · D:{b.draft}m</div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section>
          <h3 className={`${pmSectionTitleClass} mb-2`}>Tariffs & charges</h3>
          {summary.chargeCount === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">No charges defined — add them under Rules & Charges.</p>
          ) : (
            <div className="space-y-4">
              {Object.entries(chargesByCategory as Record<string, any[]>)
                .filter(([, items]) => items.length > 0)
                .map(([category, items]) => (
                  <div key={category}>
                    <h4 className="mb-2 text-xs font-semibold text-[#0088cc]">
                      {CHARGE_LABELS[category] || category}
                    </h4>
                    <div className="space-y-1">
                      {items.map((c: any) => (
                        <div key={c._id} className="flex items-center justify-between rounded-lg bg-[hsl(var(--muted))]/50 px-3 py-2 text-sm">
                          <div>
                            <span className="font-medium text-[hsl(var(--foreground))]">{c.chargeName}</span>
                            {c.description && <p className="mt-0.5 text-xs text-[hsl(var(--muted-foreground))]">{c.description}</p>}
                          </div>
                          <div className="ml-4 shrink-0 text-right">
                            <span className="font-semibold text-[#0088cc]">{c.amount} {c.currency}</span>
                            {c.unit && <div className="text-xs text-[hsl(var(--muted-foreground))]">{c.unit}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </section>

        {rules.length > 0 && (
          <section>
            <h3 className={`${pmSectionTitleClass} mb-2`}>Port rules ({rules.length})</h3>
            <div className="space-y-2">
              {rules.map((r: any) => (
                <div key={r._id} className={pmCardClass}>
                  <span className="text-xs font-medium text-[#0088cc]">{r.ruleType}</span>
                  <p className="mt-1 text-sm text-[hsl(var(--foreground))]">{r.description}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {(agents.length > 0 || warehouses.length > 0) && (
          <section className="grid grid-cols-2 gap-4">
            {agents.length > 0 && (
              <div>
                <h3 className={`${pmSectionTitleClass} mb-2`}>Agents</h3>
                {agents.map((a: any) => (
                  <div key={a._id} className="text-sm font-medium text-[hsl(var(--foreground))]">{a.companyName || a.name}</div>
                ))}
              </div>
            )}
            {warehouses.length > 0 && (
              <div>
                <h3 className={`${pmSectionTitleClass} mb-2`}>Warehouses</h3>
                {warehouses.map((w: any) => (
                  <div key={w._id} className="text-sm font-medium text-[hsl(var(--foreground))]">{w.name}</div>
                ))}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
